import { z } from 'zod';
import { RelayStatusSchema } from './relay';
/**
 * Auction SESSION contract — the schedule screen's unit of work.
 *
 * A session is one auction house closing one batch of lots in one sitting:
 * "AXA Car Auction, 16 September, from 08:00, 42 lots, 1 of them yours". It is
 * a derived aggregate, not a stored collection — `apps/api`'s auction-sessions
 * module groups the cars collection by (house, calendar day, closing HOUR) and
 * joins the caller's own bids for `myBids`.
 *
 * WHY THE HOUR AND NOT THE EXACT MINUTE: lots do not close in lockstep.
 * Measured against the live collection, grouping on `HH:mm` shatters 595 lots
 * into 262 buckets of which only 68 hold more than one — i.e. it does not
 * describe sessions at all, it just relabels lots. Grouping on the hour yields
 * 70 buckets, 43 of them shared, up to 49 lots each, which is the shape the
 * schedule is drawn around. `time` still reports the real first close, so a
 * lone lot keeps its own minute.
 *
 * WHY THIS IS NOT `AuctionScheduleItem`: the older schedule contract in
 * `apps/auction/src/features/auction-schedule/types` is PER CAR and is fed by
 * the admin-only `GET /admin/auction-schedule`, which starts from the `bids`
 * collection and therefore only ever sees lots somebody already bid on. The
 * schedule screen has to show every upcoming closing, grouped by house, to a
 * signed-in non-admin user. Those are different questions, so they get
 * different endpoints; the admin one is deliberately left untouched.
 *
 * TIME IS SPLIT ON PURPOSE. `date` is the calendar day in the DISPLAY zone
 * (Europe/Kyiv) and `time` is the wall clock in that same zone, both already
 * resolved server-side. The screen groups by calendar day, and a UTC instant
 * cannot be grouped into days without first choosing a zone — doing that in
 * the browser would put a lot into a different cell depending on where the
 * viewer sits. `endsAt` carries the unambiguous instant for countdowns.
 */
/**
 * Stable identifier for an auction house, derived from `auction_name`.
 *
 * Deliberately a free string, not an enum: `auction_name` is scraped upstream
 * text, so a new house appears in the data before anybody adds it to a union,
 * and an enum would make that a validation failure instead of a new row.
 *
 * HONESTY NOTE: `auction_name` is best-effort in the scraper — it is read from
 * an optional element and is currently absent on every car in the collection.
 * Sessions therefore fall back to grouping by `data_source`, which is always
 * present, so the schedule shows one real house ("Alpicar") today and splits
 * into named houses on its own as soon as the scraper starts filling the
 * field. Nothing downstream needs to change when that happens.
 */
export const AuctionHouseKeySchema = z.string().min(1);
export const AuctionHouseSchema = z.object({
    /** Slug of `auction_name` — stable across renames of the display label. */
    key: AuctionHouseKeySchema,
    /** `auction_name` as scraped, shown in full in the day panel and timeline. */
    name: z.string(),
    /**
     * Short label for the filter chips and the mobile timeline's left column,
     * where the full name does not fit. Server-derived (first word), so every
     * client shortens a house the same way.
     */
    shortName: z.string(),
});
/**
 * One lot WITH CLIENT BIDS inside a session — served only on the admin's
 * `GET /auction-sessions?scope=bids` (owner decision 2026-10-12: the admin
 * schedule shows the lots clients actually bid on, not the whole catalogue).
 *
 * "Standing" bids only: `Under Review`, `Outbid`, `Won`. `Not Won` (cancelled,
 * withdrawn, upstream-rejected) is excluded, so a lot whose every bid was
 * cancelled drops off the schedule.
 */
export const AuctionSessionBidLotSchema = z.object({
    /** `String(car._id)` — the same id `bids.carId` carries. */
    carId: z.string(),
    listingId: z.string().nullable(),
    /** Make/model/year, or null when the lot carries none of them. */
    title: z.string().nullable(),
    /**
     * The lot's card photo (`lotCardImageUrl`), or null when it has none.
     * Optional for a reader of an older producer.
     */
    imageUrl: z.string().nullable().optional(),
    /** The lot's close: `auction_end_date`, else the legacy `end_date`. */
    closesAt: z.coerce.date(),
    /** `HH:mm` of `closesAt` in Europe/Kyiv. */
    time: z.string().regex(/^\d{2}:\d{2}$/),
    /** Standing client bids on the lot. */
    bidCount: z.number().int().positive(),
    /** The leading bid: a `Won` bid first, else the highest amount (earliest on a tie). */
    leader: z.object({
        userId: z.string(),
        /** `users.name`, else `users.email`, else null (user gone). Admin-only payload. */
        label: z.string().nullable(),
        amount: z.number(),
        /** User-facing status of the leading bid (`Under Review` / `Outbid` / `Won`). */
        status: z.string(),
        /** Back-office relay state of the leading bid (absent on the document = `captured`). */
        relayStatus: RelayStatusSchema,
    }),
});
export const AuctionSessionSchema = z.object({
    /**
     * `${house.key}:${date}:${time}` — stable across refetches, so React keys
     * and the selected-day panel survive a poll that reorders the array.
     */
    id: z.string(),
    house: AuctionHouseSchema,
    /** Calendar day in Europe/Kyiv, `YYYY-MM-DD`. Group by this, never by `endsAt`. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /**
     * Wall clock in Europe/Kyiv, `HH:mm` — when the session's FIRST lot closes.
     * This is the time the screen prints next to the house. A one-lot session
     * therefore shows that lot's real minute (`08:42`) while a busy one shows
     * the top of its hour (`09:00`), which is what the artboards draw.
     */
    time: z.string().regex(/^\d{2}:\d{2}$/),
    /** Instant the first lot closes — what a countdown counts down to. */
    startsAt: z.coerce.date(),
    /**
     * Instant the LAST lot closes. A session is over only once this has passed;
     * using the first close here would grey out a session while lots in it are
     * still live.
     */
    endsAt: z.coerce.date(),
    /** Lots closing in this session. */
    lots: z.number().int().nonnegative(),
    /**
     * Lots in this session the CALLER has an active bid on. Always 0 for an
     * anonymous request — never another user's count.
     */
    myBids: z.number().int().nonnegative(),
    /**
     * ADDITIVE, `scope=bids` only: the session's lots with client bids, ordered
     * by close. Absent on the default (catalogue) response.
     */
    bidLots: z.array(AuctionSessionBidLotSchema).optional(),
    /** ADDITIVE, `scope=bids` only: standing client bids across `bidLots`. */
    bids: z.number().int().nonnegative().optional(),
});
/**
 * Counters the schedule's four stat cards read.
 *
 * Computed server-side over the WHOLE window rather than in the browser,
 * because the browser only ever holds the sessions for the months it has
 * fetched — deriving "this week" from a partial array silently undercounts
 * whenever the week straddles a month boundary.
 */
export const AuctionSessionStatsSchema = z.object({
    /** Sessions closing in the requested window. */
    sessionsInWindow: z.number().int().nonnegative(),
    /** Lots across sessions that have not closed yet. */
    activeLots: z.number().int().nonnegative(),
    /** Lots closing today (Europe/Kyiv). */
    lotsToday: z.number().int().nonnegative(),
    /** Lots closing in the next 7 days, and how many sessions carry them. */
    lotsThisWeek: z.number().int().nonnegative(),
    sessionsThisWeek: z.number().int().nonnegative(),
    /** Earliest still-open session, for the page eyebrow. Null when none remain. */
    nextSessionAt: z.coerce.date().nullable(),
});
export const AuctionSessionsResponseSchema = z.object({
    data: z.array(AuctionSessionSchema),
    /** Every house present in the window, so a filter chip can be drawn for a house with zero sessions. */
    houses: z.array(AuctionHouseSchema),
    stats: AuctionSessionStatsSchema,
    /** Inclusive window actually served, echoed back so the client can cache by range. */
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    lastUpdated: z.coerce.date(),
});
/** The display zone the server resolves `date`/`time` in. */
export const AUCTION_SCHEDULE_TIME_ZONE = 'Europe/Kyiv';
/**
 * Days the car transporter leaves Switzerland (Wednesday, Thursday).
 *
 * This lives in the contract rather than in the screen because the schedule
 * marks these days and the landed-cost copy quotes the same 3–4 day transit
 * off them; two hard-coded copies would drift.
 */
export const TRANSPORTER_DEPARTURE_WEEKDAYS = [3, 4];
/** True when `date` (`YYYY-MM-DD`) is a transporter departure day. */
export function isTransporterDepartureDay(date) {
    // Parsed as UTC midnight on purpose: `new Date('2026-09-16')` is UTC, and
    // getUTCDay() therefore returns the weekday of the calendar day itself,
    // free of the viewer's offset. getDay() here would shift the answer by one
    // for anybody west of UTC.
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return TRANSPORTER_DEPARTURE_WEEKDAYS.includes(weekday);
}
//# sourceMappingURL=auction-session.js.map